package org.example.project.planner.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.client.model.ActivityResponse
import org.example.project.ui.components.AgeBadge
import org.example.project.ui.components.CategoryBadge
import org.example.project.ui.components.DurationBadge
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.LocalPlayfulShapes
import org.example.project.ui.theme.PlayfulColors

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun CatalogPickerDialog(
    activities: List<ActivityResponse>,
    isLoading: Boolean,
    onSearch: (String) -> Unit,
    onSelect: (ActivityResponse) -> Unit,
    onOpenFavorites: () -> Unit,
    onDismiss: () -> Unit
) {
    val shapes = LocalPlayfulShapes.current
    var searchQuery by remember { mutableStateOf("") }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Column(modifier = Modifier.fillMaxWidth()) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = AppIcons.Catalog,
                        contentDescription = null,
                        tint = PlayfulColors.TealDark,
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = "Wybierz zabawę z katalogu",
                        fontSize = 17.sp,
                        fontWeight = FontWeight.Bold,
                        color = PlayfulColors.TextPrimary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f)
                    )
                }

                Spacer(modifier = Modifier.height(6.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "Baza gotowych scenariuszy",
                        fontSize = 12.sp,
                        color = PlayfulColors.TextSecondary
                    )

                    Surface(
                        color = PlayfulColors.SunYellowLight,
                        shape = shapes.buttonSmall,
                        border = BorderStroke(1.dp, PlayfulColors.SunYellowBorder),
                        modifier = Modifier
                            .clip(shapes.buttonSmall)
                            .clickable { onOpenFavorites() }
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                        ) {
                            Icon(
                                imageVector = AppIcons.StarFilled,
                                contentDescription = null,
                                tint = PlayfulColors.SunYellowDark,
                                modifier = Modifier.size(14.dp)
                            )
                            Spacer(modifier = Modifier.width(4.dp))
                            Text(
                                text = "Ulubione",
                                fontSize = 12.sp,
                                fontWeight = FontWeight.SemiBold,
                                color = PlayfulColors.SunYellowDark
                            )
                        }
                    }
                }
            }
        },
        text = {
            Column(modifier = Modifier.fillMaxWidth()) {
                // Search field
                OutlinedTextField(
                    value = searchQuery,
                    onValueChange = {
                        searchQuery = it
                        onSearch(it)
                    },
                    placeholder = { Text("Szukaj zabawy w katalogu...", fontSize = 12.sp) },
                    leadingIcon = {
                        Icon(
                            imageVector = AppIcons.Search,
                            contentDescription = null,
                            tint = PlayfulColors.TextSecondary,
                            modifier = Modifier.size(18.dp)
                        )
                    },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = shapes.input,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = PlayfulColors.Coral,
                        unfocusedBorderColor = PlayfulColors.Border
                    )
                )

                Spacer(modifier = Modifier.height(10.dp))

                if (isLoading) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(200.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(
                            color = PlayfulColors.Coral,
                            strokeWidth = 2.dp
                        )
                    }
                } else if (activities.isEmpty()) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(200.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Surface(
                                color = PlayfulColors.TealLight,
                                shape = shapes.pill,
                                modifier = Modifier.size(44.dp)
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    Icon(
                                        imageVector = AppIcons.Catalog,
                                        contentDescription = null,
                                        tint = PlayfulColors.TealDark,
                                        modifier = Modifier.size(22.dp)
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.height(8.dp))
                            Text(
                                text = "Brak pasujących zabaw w katalogu",
                                fontWeight = FontWeight.Bold,
                                fontSize = 14.sp,
                                color = PlayfulColors.TextPrimary
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            Text(
                                text = "Zmień zapytanie lub wybierz inną opcję.",
                                textAlign = TextAlign.Center,
                                fontSize = 12.sp,
                                color = PlayfulColors.TextSecondary
                            )
                        }
                    }
                } else {
                    LazyColumn(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(320.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        items(activities, key = { it.id }) { activity ->
                            Card(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable { onSelect(activity) },
                                shape = shapes.cardSmall,
                                colors = CardDefaults.cardColors(containerColor = PlayfulColors.Surface),
                                border = BorderStroke(1.dp, PlayfulColors.BorderSubtle),
                                elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
                            ) {
                                Column(modifier = Modifier.padding(12.dp)) {
                                    Text(
                                        text = activity.name,
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 14.sp,
                                        color = PlayfulColors.TextPrimary
                                    )
                                    if (activity.description.isNotBlank()) {
                                        Spacer(modifier = Modifier.height(2.dp))
                                        Text(
                                            text = activity.description,
                                            fontSize = 11.sp,
                                            color = PlayfulColors.TextSecondary,
                                            maxLines = 2
                                        )
                                    }
                                    Spacer(modifier = Modifier.height(6.dp))
                                    FlowRow(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                                        verticalArrangement = Arrangement.spacedBy(4.dp)
                                    ) {
                                        CategoryBadge(category = activity.category)
                                        AgeBadge(minAge = activity.minAge, maxAge = activity.maxAge)
                                        DurationBadge(durationMinutes = activity.durationMinutes)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = onDismiss,
                shape = shapes.buttonSmall
            ) {
                Text("Zamknij", fontSize = 13.sp, color = PlayfulColors.TextSecondary)
            }
        },
        shape = shapes.dialog,
        containerColor = PlayfulColors.Surface
    )
}
